import { FC, useState, useCallback, useRef, useEffect } from 'react';
import { Spinner } from '@chakra-ui/react';
import { CaretDown, MagnifyingGlass, VideoCamera } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import { useEndpoints } from '../../../hooks/useEndpoints';
import { IProgrammingExercise, IProgrammingGroupExercise } from '../../../models/programming';
import Typhography from '../../../shared/Typhography/Typhography';
import style from './ExerciseSearchPanel.module.scss';

// ────────────────────────────────────────────────────────────────────────────────
// Constants
// ────────────────────────────────────────────────────────────────────────────────

const MIN_SEARCH_CHARS = 2;
// Limite alto allineato alla libreria: niente più troncamento rigido a 20 che faceva
// divergere il modal dalla libreria (bug b3e58e). I muscoli ora filtrano server-side.
const FETCH_LIMIT = 100;
// Limite più alto quando serve il filtro client-side multi-gruppo (l'endpoint accetta 1 gruppo).
const FETCH_LIMIT_MULTIGROUP = 300;
const DEBOUNCE_MS = 300;

const GROUP_COLORS = [
  { bg: '#effafe', text: '#1a8fb5', border: '#d4eef7' },
  { bg: '#e6f5f4', text: '#1a8f6e', border: '#c8e8e5' },
  { bg: '#fff3e0', text: '#d4860b', border: '#ffe0b2' },
  { bg: '#fce4ec', text: '#c62828', border: '#f8bbd0' },
  { bg: '#f3e5f5', text: '#7b1fa2', border: '#e1bee7' },
  { bg: '#e8eaf6', text: '#3949ab', border: '#c5cae9' },
  { bg: '#e0f2f1', text: '#00796b', border: '#b2dfdb' },
  { bg: '#fff8e1', text: '#f9a825', border: '#ffecb3' },
];

const MUSCLE_LABEL: Record<string, string> = {
  glutes: 'Glutei',
  quadriceps: 'Quadricipiti',
  hamstrings: 'Ischiocrurali',
  calves: 'Polpacci',
  hip_flexors: "Flessori dell'anca",
  adductors: 'Adduttori',
  abductors: 'Abduttori',
  tibialis_anterior: 'Tibiale anteriore',
  pectorals: 'Pettorali',
  anterior_deltoid: 'Deltoide anteriore',
  lateral_deltoid: 'Deltoide laterale',
  posterior_deltoid: 'Deltoide posteriore',
  trapezius: 'Trapezio',
  latissimus_dorsi: 'Gran dorsale',
  rhomboids: 'Romboidi',
  erector_spinae: 'Erettori spinali',
  biceps: 'Bicipiti',
  triceps: 'Tricipiti',
  forearms: 'Avambracci',
  rotator_cuff: 'Cuffia dei rotatori',
  rectus_abdominis: 'Retto addominale',
  obliques: 'Obliqui',
  transverse_abdominis: 'Trasverso addominale',
  hip_rotators: "Rotatori dell'anca",
  neck_flexors: 'Flessori del collo',
  neck_extensors: 'Estensori del collo',
};

const ALL_MUSCLES = Object.keys(MUSCLE_LABEL);

// ────────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────────

const getExName = (ex: IProgrammingExercise): string => ex.name.it || ex.name.en || '';

const getGroupName = (g: IProgrammingGroupExercise): string => g.name.it || g.name.en || '';

// Keep for future use / type safety — color data stays co-located with GROUP_COLORS
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getGroupColor = (index: number) => GROUP_COLORS[index % GROUP_COLORS.length];

// ────────────────────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────────────────────

export interface ExerciseSearchPanelProps {
  onSelect: (exercise: IProgrammingExercise) => void;
}

const ExerciseSearchPanel: FC<ExerciseSearchPanelProps> = ({ onSelect }) => {
  const { getProgrammingExercises, getProgrammingExerciseGroups } = useEndpoints();

  // ── State ──
  const [query, setQuery] = useState('');
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [selectedMuscles, setSelectedMuscles] = useState<string[]>([]);
  const [muscleDropdownOpen, setMuscleDropdownOpen] = useState(false);
  const [exercises, setExercises] = useState<IProgrammingExercise[]>([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [loading, setLoading] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const muscleDropdownRef = useRef<HTMLDivElement>(null);

  // ── Groups query (cached) ──
  const groupsQuery = useQuery({
    queryKey: ['programming-exercise-groups'],
    queryFn: () => getProgrammingExerciseGroups().then((r) => r.data),
    staleTime: 10 * 60 * 1000,
  });

  const groups: IProgrammingGroupExercise[] = groupsQuery.data ?? [];

  // ── Build a stable color map: groupId → color index ──
  const groupColorMap = useRef<Record<string, number>>({});
  groups.forEach((g, i) => {
    if (!(g._id in groupColorMap.current)) {
      groupColorMap.current[g._id] = i;
    }
  });

  // ── Close muscle dropdown on outside click ──
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (muscleDropdownRef.current && !muscleDropdownRef.current.contains(e.target as Node)) {
        setMuscleDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── Fetch exercises whenever query, selectedGroupIds or selectedMuscles changes ──
  const fetchExercises = useCallback(
    (searchText: string, groupIds: string[], muscles: string[]) => {
      if (timerRef.current) clearTimeout(timerRef.current);

      const hasSearch = searchText.length >= MIN_SEARCH_CHARS;
      const hasGroup = groupIds.length > 0;
      const hasMuscle = muscles.length > 0;

      if (!hasSearch && !hasGroup && !hasMuscle) {
        setExercises([]);
        setTotalDocs(0);
        setLoading(false);
        return;
      }

      timerRef.current = setTimeout(async () => {
        try {
          setLoading(true);
          // Multi-gruppo è l'unico filtro ancora client-side (l'endpoint accetta 1 gruppo):
          // serve un limit più alto per non perdere match. Muscoli e singolo gruppo: server-side.
          const isMultiGroup = groupIds.length > 1;
          const limit = isMultiGroup ? FETCH_LIMIT_MULTIGROUP : FETCH_LIMIT;
          const params: Record<string, any> = { lang: 'it', limit };
          if (hasSearch) params.search = searchText;
          // Server-side group solo se esattamente 1 gruppo selezionato
          if (groupIds.length === 1) params.group = groupIds[0];
          // Muscoli server-side: codici separati da virgola (niente più filtro client troncato)
          if (hasMuscle) params.muscles = muscles.join(',');

          const { data } = await getProgrammingExercises(params);
          let results = data.docs;

          // Filtro client-side SOLO per multi-group (OR tra gruppi)
          if (isMultiGroup) {
            results = results.filter((ex) =>
              ex.groups.some((g) => groupIds.includes(g._id)),
            );
            setTotalDocs(results.length);
          } else {
            setTotalDocs(data.totalDocs ?? results.length);
          }

          setExercises(results);
        } catch {
          setExercises([]);
          setTotalDocs(0);
        } finally {
          setLoading(false);
        }
      }, DEBOUNCE_MS);
    },
    [getProgrammingExercises],
  );

  useEffect(() => {
    fetchExercises(query, selectedGroupIds, selectedMuscles);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, selectedGroupIds, selectedMuscles]);

  // ── Handlers ──
  const handleQueryChange = useCallback((value: string) => {
    setQuery(value);
  }, []);

  const handleGroupPill = useCallback((groupId: string) => {
    setSelectedGroupIds((prev) =>
      prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId],
    );
  }, []);

  const handleGroupReset = useCallback(() => {
    setSelectedGroupIds([]);
  }, []);

  const handleMuscleToggle = useCallback((muscle: string) => {
    setSelectedMuscles((prev) =>
      prev.includes(muscle) ? prev.filter((m) => m !== muscle) : [...prev, muscle],
    );
  }, []);

  const handleRemoveMuscle = useCallback((muscle: string) => {
    setSelectedMuscles((prev) => prev.filter((m) => m !== muscle));
  }, []);

  const handleRemoveGroup = useCallback((groupId: string) => {
    setSelectedGroupIds((prev) => prev.filter((id) => id !== groupId));
  }, []);

  const handleResetAll = useCallback(() => {
    setQuery('');
    setSelectedGroupIds([]);
    setSelectedMuscles([]);
  }, []);

  const handleSelect = useCallback(
    (ex: IProgrammingExercise) => {
      onSelect(ex);
    },
    [onSelect],
  );

  // ── Derived ──
  const hasActiveFilters =
    query.length > 0 || selectedGroupIds.length > 0 || selectedMuscles.length > 0;
  const showHint = !hasActiveFilters;
  const showEmptyState = !loading && !showHint && exercises.length === 0;

  // ────────────────────────────────────────────────────────────────────────────
  // Render
  // ────────────────────────────────────────────────────────────────────────────

  return (
    <div className={style.panel}>

      {/* ── 1. Search bar ── */}
      <div className={style.searchWrapper}>
        <MagnifyingGlass size={16} className={style.searchIcon} />
        <input
          type="text"
          placeholder="Cerca esercizio..."
          value={query}
          onChange={(e) => handleQueryChange(e.target.value)}
          onFocus={() => setInputFocused(true)}
          onBlur={() => setInputFocused(false)}
          className={classNames(style.searchInput, { [style.searchInputFocused]: inputFocused })}
          aria-label="Cerca esercizio"
        />
        {loading && (
          <span className={style.spinnerBox}>
            <Spinner size="xs" />
          </span>
        )}
      </div>

      {/* ── 2. Group pills row ── */}
      {groupsQuery.isLoading ? (
        <div className="d-flex gap-2 overflow-hidden">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className={style.pillSkeleton}>&nbsp;</div>
          ))}
        </div>
      ) : (
        <div className={style.pillsRow}>
          {/* "Tutti" pill */}
          <button
            type="button"
            className={classNames(style.pillBase, {
              [style.pillAllActive]: selectedGroupIds.length === 0,
              [style.pillAll]: selectedGroupIds.length > 0,
            })}
            onClick={handleGroupReset}
          >
            Tutti
          </button>

          {groups.map((group) => {
            const colorIdx = groupColorMap.current[group._id] ?? 0;
            const isActive = selectedGroupIds.includes(group._id);
            return (
              <button
                key={group._id}
                type="button"
                className={classNames(style.groupPill, { [style.groupPillActive]: isActive })}
                data-color-idx={String(colorIdx % GROUP_COLORS.length)}
                onClick={() => handleGroupPill(group._id)}
              >
                {getGroupName(group)}
              </button>
            );
          })}
        </div>
      )}

      {/* ── 3. Muscle filter dropdown ── */}
      <div className={style.muscleFilterWrapper} ref={muscleDropdownRef}>
        <button
          type="button"
          className={classNames(style.muscleDropdownTrigger, {
            [style.muscleDropdownTriggerActive]: selectedMuscles.length > 0,
          })}
          onClick={() => setMuscleDropdownOpen((o) => !o)}
        >
          <span>
            {selectedMuscles.length === 0
              ? 'Muscoli'
              : `Muscoli (${selectedMuscles.length})`}
          </span>
          <CaretDown
            size={12}
            weight="bold"
            className={classNames(style.caretIcon, { [style.caretOpen]: muscleDropdownOpen })}
          />
        </button>

        {muscleDropdownOpen && (
          <div className={style.muscleDropdownMenu}>
            {ALL_MUSCLES.map((muscle) => {
              const checked = selectedMuscles.includes(muscle);
              return (
                <label key={muscle} className={style.muscleOption}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => handleMuscleToggle(muscle)}
                    className={style.muscleCheckbox}
                  />
                  <span>{MUSCLE_LABEL[muscle]}</span>
                </label>
              );
            })}
          </div>
        )}
      </div>

      {/* ── 4. Active filter chips ── */}
      {hasActiveFilters && (
        <div className={style.activeFiltersRow}>
          {query.length > 0 && (
            <span className={style.filterChip}>
              "{query}"
              <button
                type="button"
                className={style.chipRemove}
                onClick={() => setQuery('')}
                aria-label="Rimuovi ricerca"
              >
                ×
              </button>
            </span>
          )}
          {selectedGroupIds.map((gid) => {
            const group = groups.find((g) => g._id === gid);
            if (!group) return null;
            return (
              <span key={gid} className={style.filterChip}>
                {getGroupName(group)}
                <button
                  type="button"
                  className={style.chipRemove}
                  onClick={() => handleRemoveGroup(gid)}
                  aria-label={`Rimuovi gruppo ${getGroupName(group)}`}
                >
                  ×
                </button>
              </span>
            );
          })}
          {selectedMuscles.map((m) => (
            <span key={m} className={style.filterChip}>
              {MUSCLE_LABEL[m]}
              <button
                type="button"
                className={style.chipRemove}
                onClick={() => handleRemoveMuscle(m)}
                aria-label={`Rimuovi muscolo ${MUSCLE_LABEL[m]}`}
              >
                ×
              </button>
            </span>
          ))}
          <button
            type="button"
            className={style.resetAllBtn}
            onClick={handleResetAll}
          >
            Reset tutto
          </button>
        </div>
      )}

      {/* ── 5. Exercise list ── */}
      {showHint ? (
        <Typhography type="p" size="regular-12" className={`${style.hint} color-text-2`}>
          Cerca per nome, seleziona un gruppo o filtra per muscolo
        </Typhography>
      ) : loading ? (
        <div className={style.stateBox}>
          <Spinner size="sm" />
          <Typhography type="span" size="regular-14" className="color-text-2">
            Caricamento...
          </Typhography>
        </div>
      ) : showEmptyState ? (
        <div className={style.stateBox}>
          <Typhography type="span" size="regular-14" className="color-text-2">
            Nessun esercizio trovato
          </Typhography>
        </div>
      ) : (
        <div className={style.listWrapper}>
          <Typhography type="span" size="regular-12" className={`${style.resultCount} color-text-2`}>
            {totalDocs > exercises.length
              ? `${exercises.length} di ${totalDocs} — affina la ricerca per restringere`
              : `${exercises.length} ${exercises.length === 1 ? 'esercizio' : 'esercizi'}`}
          </Typhography>
          {exercises.map((ex) => {
            const hasVideo = !!ex.url;
            return (
              <div
                key={ex._id}
                className={style.exerciseItem}
                onClick={() => handleSelect(ex)}
              >
                <Typhography type="span" size="medium-14" className={style.exerciseName}>
                  {getExName(ex)}
                </Typhography>

                {/* Group badges — show max 2 to avoid overflow */}
                {ex.groups.slice(0, 2).map((g) => {
                  const colorIdx = groupColorMap.current[g._id] ?? 0;
                  return (
                    <span
                      key={g._id}
                      className={style.groupBadge}
                      data-color-idx={String(colorIdx % GROUP_COLORS.length)}
                    >
                      {getGroupName(g)}
                    </span>
                  );
                })}

                {hasVideo && <VideoCamera size={14} weight="fill" className={style.videoIcon} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ExerciseSearchPanel;
